/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosRequestConfig } from 'axios'
import create from 'zustand'
import _ from 'lodash'

import { Configuration } from '../../redux/configuration'
import { IClientData } from '../../redux/clientData'
import { ILogin } from '../../redux/login'
import { ErrorData } from '../../interfaces'
import sendAddress from '../../services/sendAddress'
import sendClient from '../../services/sendClient'
import sendTo from '../../services/sendTo'
import fetchAddressDataFromMiddleware, { AddressResponse } from '../../services/fetchAddressData'

type State = {
  currentStep: number
  clientData: IClientData
  loginData: ILogin
  addressData: AddressResponse | null
  isLoading: boolean
  redirectSuccessful: boolean
  redirectLogin: boolean
  initialData?: any
  formErrors?: any
  previousStep: () => void
  nextStep: () => void
  loadForm: (clientData: IClientData, initialData?: any) => void
  loadAddress: (zipCode: string, middleware?: AxiosRequestConfig) => void
  submitForm: (formData: any, submitData: any, configuration: Configuration | null) => Promise<boolean>
  submitAddressForm: (formData: any, submitData: any, configuration: Configuration | null) => Promise<boolean>
}

function getId(value: string): string {
  const [, id] = value.split('$.')
  return id
}

const submitActions = {
  some: (formData: any, ids: string[]) => {
    return ids.some((id) => formData[getId(id)])
  },
}

function deepMapValues(obj: any, mapper: any): any {
  return _.mapValues(obj, (value) => {
    if (_.isPlainObject(value)) {
      return deepMapValues(value, mapper)
    }
    if (Array.isArray(value) && value.every((item) => _.isPlainObject(item))) {
      return value.map((item) => deepMapValues(item, mapper))
    }
    return mapper(value)
  })
}

function transformPayload(formData: any, submitData: any) {
  return deepMapValues(submitData, (value: any) => {
    if (typeof value === 'string' && value.startsWith('$.')) {
      return formData[getId(value)]
    }
    if (!Array.isArray(value)) {
      return value
    }
    const [func, id] = value
    const regex = new RegExp(func)
    const data = formData[getId(id)]
    if (regex.test(data)) {
      return regex.exec(data)?.[0]
    }
    const [, ...ids] = value
    return submitActions[func as keyof typeof submitActions](formData, ids)
  })
}

function transformErrorData(errorData: ErrorData) {
  return Object.entries(errorData.details.customer || {}).reduce((agg, [key, value]) => {
    const [{ message }] = value
    return {
      ...agg,
      [key]: { type: 'custom', message },
    }
  }, {})
}

export const useStore = create<State>((set, get) => ({
  currentStep: 1,
  isLoading: false,
  redirectSuccessful: false,
  redirectLogin: false,
  clientData: {} as IClientData,
  loginData: {} as ILogin,
  addressData: null,
  previousStep: () => set((state) => ({ currentStep: state.currentStep - 1 })),
  nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
  loadForm: (clientData, initialData) => {
    if (!clientData || Object.values(clientData).some((value) => !value)) {
      return
    }
    set({
      clientData,
      initialData:
        initialData &&
        _.mapValues(initialData, (value) => {
          return clientData[value as keyof typeof clientData]
        }),
    })
  },
  loadAddress: async (zipCode, middleware) => {
    const { loginData } = get()

    set({ isLoading: true })
    const { data, success, status } = await fetchAddressDataFromMiddleware(zipCode, loginData.accessToken, middleware)

    if (success) {
      set({ isLoading: false, addressData: data as AddressResponse })
      return
    }

    let formErrors
    if (status === 404) {
      formErrors = {
        cep: { type: 'custom', message: 'CEP inválido' },
      }
    }

    set({ isLoading: false, formErrors })
  },
  submitForm: async (formData, submitData, configuration) => {
    const { sendClientMiddleware } = configuration?.extensions || {}

    set({ isLoading: true })
    const payload = transformPayload(formData, submitData)

    const { data, success } = await sendClient(payload, sendClientMiddleware)
    if (success) {
      const loginData = { ...(data as ILogin), success }
      set({ isLoading: false, loginData })

      return true
    }

    const errorData = data as ErrorData
    if (errorData.details.error === 'firebase UID already assigned') {
      set({ isLoading: false, redirectLogin: true })
    } else {
      const formErrors = transformErrorData(errorData)
      set({ isLoading: false, formErrors })
    }

    return false
  },
  submitAddressForm: async (formData, submitData, configuration) => {
    const { sendAddressMiddleware, sendToMiddleware } = configuration?.extensions || {}
    const { clientData, loginData } = get()

    set({ isLoading: true })
    const payload = transformPayload(formData, submitData)

    const { data, success } = await sendAddress(payload, loginData.accessToken, sendAddressMiddleware)
    if (success) {
      const { success: redirectSuccessful } = await sendTo(clientData.phoneNumber, loginData, sendToMiddleware)
      set({ isLoading: false, redirectSuccessful })

      return true
    }

    const formErrors = transformErrorData(data as ErrorData)
    set({ isLoading: false, formErrors })

    return false
  },
}))
